import React from 'react'
import { useAppSelector } from '../../hooks/redux'

import { getNotifications } from './helpers'
import { countUnread } from '../../utilities'
import { selectConvo } from '../../slices/convo'
import { selectDMs } from '../../slices/dms'
import { selectNotification } from '../../slices/notification'
import { useEnvironment } from '../EnvironmentProvider'
import { useSocket } from '../SocketProvider'
import { useReadStatus } from '../ReadStatusProvider'
import useWindowFocus from '../../hooks/useWindowFocus'

export const NotificationsContext = React.createContext(null)

const TITLE_DELAY = 1500

const NotificationCountProvider = ({ children }) => {
  const isFocused = useWindowFocus()

  const { allConversations } = useAppSelector(selectConvo)
  const { dms, selectedConvo } = useAppSelector(selectDMs)
  const mentions = useAppSelector(selectNotification)

  const { user } = useEnvironment()
  const {
    peerChat: { newPush }
  } = useSocket()
  const { convos, wholeConvos } = useReadStatus()

  // Notification counts
  const dashboardNotifs = React.useMemo(() => {
    if (Object.keys(allConversations).length === 0) {
      return 0
    }

    const notifications = getNotifications({
      obj: allConversations,
      you: user?.id,
      convos,
      newChat: newPush ?? [],
      selected: selectedConvo,
      wholeConvos
    })
    return notifications
  }, [allConversations, user, convos, newPush, selectedConvo, wholeConvos])
  const peerNotifs = React.useMemo(() => {
    if (Object.keys(dms).length === 0) {
      return 0
    }

    const peerNotifications = getNotifications({
      obj: dms,
      you: user?.id,
      convos,
      newChat: newPush ?? [],
      selected: selectedConvo,
      wholeConvos
    })
    return peerNotifications
  }, [dms, user, convos, newPush, selectedConvo, wholeConvos])

  // Update mention notification count
  const mentionNotifs = React.useMemo(
    () => Object.keys(mentions.unread).length,
    [mentions.unread]
  )

  // For title state
  const prevNum = React.useRef(peerNotifs + dashboardNotifs + mentionNotifs)
  const timer = React.useRef(null)

  const REGULAR_STATE = React.useMemo(() => {
    const total = peerNotifs + dashboardNotifs + mentionNotifs
    const baseTitle = 'SonarCX Agent Workspace'

    if (total <= 0) {
      return baseTitle
    }

    return `(${total}) ${baseTitle}`
  }, [dashboardNotifs, peerNotifs, mentionNotifs])

  const [titles, setTitles] = React.useState([REGULAR_STATE])

  // Intermediate notification counts
  const notifsRef = React.useRef({
    messages: peerNotifs + dashboardNotifs,
    mentions: mentionNotifs
  })

  // toggling between regular states and new notification message
  React.useEffect(() => {
    const total = peerNotifs + dashboardNotifs + mentionNotifs

    // If there are notifications, toggle between number and 'new notifications' message if not in focus
    if (isFocused) {
      // Clearing intervals and changing title
      window.clearInterval(timer.current)
      document.title = REGULAR_STATE

      // keeping track of notifications
      prevNum.current = total
      notifsRef.current = {
        messages: peerNotifs + dashboardNotifs,
        mentions: mentionNotifs
      }

      // Returning to only show regular state
      setTitles([REGULAR_STATE])
      return
    }

    // Clears interval if all notifications are clear
    if (total === 0) {
      window.clearInterval(timer.current)
      return
    }

    if (total <= 0) {
      return
    }

    // Skip updating
    if (prevNum.current === total) {
      return
    }

    // Toggling between states
    if (notifsRef.current.messages !== peerNotifs + dashboardNotifs) {
      setTitles(title => {
        const titles = [...new Set([...title, 'You have new messages!'])]
        const unique = []

        titles.forEach(t => {
          if (!t.includes(document.title)) {
            unique.push(t)
          }
        })

        return [...unique, REGULAR_STATE]
      })
    }

    if (notifsRef.current.mentions !== mentionNotifs) {
      setTitles(title => {
        const titles = [...new Set([...title, 'You have new notifications!'])]
        const unique = []

        titles.forEach(t => {
          if (!t.includes(document.title)) {
            unique.push(t)
          }
        })

        return [...unique, REGULAR_STATE]
      })
    }
  }, [dashboardNotifs, peerNotifs, mentionNotifs, REGULAR_STATE, isFocused])

  // Handles logic for cycling the title
  React.useEffect(() => {
    if (titles.length <= 1) {
      return
    }

    // Use interval to cycle titles
    let i = 0
    timer.current = setInterval(() => {
      if (i === titles.length) {
        i = 0
      }
      document.title = titles[i]
      i++
    }, TITLE_DELAY)

    return () => {
      window.clearInterval(timer.current)
    }
  }, [titles])

  // Counts unread messages per conversation
  const updateCount = React.useCallback(
    (session, convos, msgs) => {
      const yourID = user?.id

      if (wholeConvos[session]?.cleared) {
        return countUnread(session, convos, yourID, msgs)
      }

      const latest = wholeConvos[session]?.latest_activity ?? []
      const events = msgs.filter(ev => !latest.find(la => la.id === ev.id))

      let current = countUnread(session, convos, yourID, events)
      current += wholeConvos[session]?.unread ?? 0
      return current
    },
    [wholeConvos, user]
  )

  return (
    <NotificationsContext.Provider
      value={{ dashboardNotifs, peerNotifs, updateCount }}
    >
      {children}
    </NotificationsContext.Provider>
  )
}

export default NotificationCountProvider

export const useNotificationCount = () => React.useContext(NotificationsContext)
